package org.gymly.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Check
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.PickedMediaHolder
import org.gymly.data.PortalRepo
import org.gymly.model.PickedImagePayload
import org.gymly.model.PortalClientNotification
import org.gymly.model.PortalMe
import org.gymly.model.PortalNotificationPreferences
import org.gymly.model.PortalUser
import org.gymly.theme.GymlyColors
import org.gymly.ui.components.PhotoActionBar
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.MobilePageHeader
import org.gymly.ui.components.NotificationBell
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.rememberPasswordVisible
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage

@Composable
fun ProfileScreen(
    user: PortalUser,
    mobile: Boolean = false,
    unreadCount: Long = 0,
    langEs: Boolean = true,
    onBell: () -> Unit = {},
    onLogout: () -> Unit = {},
    onNotifChanged: () -> Unit = {},
    onUserUpdated: (PortalUser) -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var me by remember { mutableStateOf<PortalMe?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    var fullName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var preferredLocale by remember { mutableStateOf(user.preferredLocale ?: "es") }
    var currentPassword by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    // Perfil → Términos y compromisos: el socio vuelve a leer los términos y cambia su permiso de WhatsApp.
    var showTerms by remember { mutableStateOf(false) }
    // Sin monitoreo de términos en el gimnasio, la tarjeta no aparece.
    var termsRequired by remember { mutableStateOf(true) }
    LaunchedEffect(Unit) {
        termsRequired = runCatching { PortalRepo.api.fetchTerms().required }.getOrDefault(true)
    }
    if (showTerms) {
        PortalTermsDetail(onBack = { showTerms = false }, onLogout = onLogout)
        return
    }
    var savingPassword by remember { mutableStateOf(false) }
    var uploadingPhoto by remember { mutableStateOf(false) }
    val photoHolder = remember { PickedMediaHolder() }
    /** Fuerza recarga de Coil cuando la URL de foto es estable (/api/portal/me/photo). */
    var photoRevision by remember { mutableStateOf(0) }
    var notifications by remember { mutableStateOf<List<PortalClientNotification>>(emptyList()) }
    var prefs by remember {
        mutableStateOf(PortalNotificationPreferences())
    }
    var savingPrefs by remember { mutableStateOf(false) }

    fun applyProfile(profile: PortalMe) {
        me = profile
        fullName = profile.displayName.ifBlank {
            listOf(profile.firstName, profile.lastName).joinToString(" ").trim()
        }
        email = profile.email.orEmpty()
        phone = profile.phone.orEmpty()
        preferredLocale = profile.preferredLocale ?: user.preferredLocale ?: "es"
    }

    fun load() {
        scope.launch {
            loading = true
            error = null
            try {
                applyProfile(PortalRepo.api.fetchMe())
                notifications = PortalRepo.api.fetchClientNotifications()
                prefs = PortalRepo.api.fetchNotificationPreferences()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar el perfil.")
            } finally {
                loading = false
            }
        }
    }

    fun saveProfile() {
        scope.launch {
            val (first, last) = splitFullName(fullName)
            if (first.isBlank()) {
                error = "Indica tu nombre."
                return@launch
            }
            saving = true
            error = null
            message = null
            try {
                val updated = PortalRepo.api.updateProfile(
                    firstName = first,
                    lastName = last.ifBlank { first },
                    email = email,
                    phone = phone,
                    preferredLocale = preferredLocale,
                )
                applyProfile(updated)
                onUserUpdated(
                    user.copy(
                        displayName = updated.displayName,
                        email = updated.email,
                        preferredLocale = updated.preferredLocale,
                    ),
                )
                message = "Perfil guardado."
            } catch (e: Exception) {
                error = e.userMessage("No se pudo guardar el perfil.")
            } finally {
                saving = false
            }
        }
    }

    fun savePassword() {
        scope.launch {
            if (currentPassword.isBlank() || newPassword.length < 8) {
                error = "La nueva contraseña debe tener al menos 8 caracteres."
                return@launch
            }
            savingPassword = true
            error = null
            message = null
            try {
                PortalRepo.api.changePassword(currentPassword, newPassword)
                currentPassword = ""
                newPassword = ""
                message = "Contraseña actualizada."
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cambiar la contraseña.")
            } finally {
                savingPassword = false
            }
        }
    }

    fun clearPhoto() {
        scope.launch {
            saving = true
            error = null
            message = null
            try {
                PortalRepo.api.deletePhoto()
                photoRevision += 1
                applyProfile(PortalRepo.api.fetchMe())
                message = "Foto eliminada."
            } catch (e: Exception) {
                error = e.userMessage("No se pudo eliminar la foto.")
            } finally {
                saving = false
            }
        }
    }

    fun uploadPhoto() {
        error = null
        message = null
        openImagePicker { picked ->
            if (picked == null) {
                photoHolder.clear()
                scope.launch { error = "No se seleccionó ninguna imagen." }
                return@openImagePicker
            }
            val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
            photoHolder.set(payload, picked.fileName)
            scope.launch {
                uploadingPhoto = true
                try {
                    val updated = PortalRepo.api.uploadPhoto(payload.bytes, payload.fileName, payload.mimeType)
                    applyProfile(updated)
                    if (updated.photoUrl.isNullOrBlank()) {
                        error = "La foto no quedó guardada. Reintenta."
                        return@launch
                    }
                    photoHolder.clear()
                    photoRevision += 1
                    message = "Foto actualizada."
                } catch (e: Exception) {
                    error = e.userMessage("No se pudo subir la foto.")
                } finally {
                    uploadingPhoto = false
                }
            }
        }
    }

    fun updatePrefs(next: PortalNotificationPreferences) {
        val previous = prefs
        prefs = next
        scope.launch {
            savingPrefs = true
            error = null
            try {
                prefs = PortalRepo.api.saveNotificationPreferences(next)
                onNotifChanged()
            } catch (e: Exception) {
                prefs = previous
                error = e.userMessage("No se pudieron guardar las preferencias.")
            } finally {
                savingPrefs = false
            }
        }
    }

    fun markRead(item: PortalClientNotification) {
        if (item.read) return
        scope.launch {
            try {
                PortalRepo.api.markClientNotificationRead(item.id)
                notifications = notifications.map {
                    if (it.id == item.id) it.copy(read = true) else it
                }
                onNotifChanged()
            } catch (_: Exception) {
                // silent — list still usable
            }
        }
    }

    fun doLogout() {
        PortalRepo.api.logout()
        onLogout()
    }

    LaunchedEffect(user.id) { load() }

    if (loading && me == null) {
        GymlyLoadingCenter()
        return
    }

    BoxWithConstraints(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
        val stacked = maxWidth < 900.dp
        val summaryWidth = if (stacked) Modifier.fillMaxWidth() else Modifier.width(280.dp)

        Column(Modifier.fillMaxSize()) {
            if (mobile) {
                MobilePageHeader(
                    title = "Perfil",
                    unreadCount = unreadCount,
                    langEs = langEs,
                    onLangEs = { },
                    onBell = onBell,
                )
            }

            Column(
                Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(GymlySpace.lg),
            ) {
                if (!mobile) {
                    Text("Perfil", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(16.dp))
                }
                error?.let {
                    Text(it, color = GymlyColors.Error, modifier = Modifier.padding(bottom = 10.dp))
                }
                message?.let {
                    Text(it, color = GymlyColors.Success, modifier = Modifier.padding(bottom = 10.dp))
                }

                if (mobile) {
                    // Celular: misma edición que en escritorio (datos, idioma y contraseña), en una columna.
                    ProfileSummaryCard(
                        me = me,
                        photoRevision = photoRevision,
                        uploadingPhoto = uploadingPhoto,
                        onPickPhoto = { uploadPhoto() },
                        onClearPhoto = { clearPhoto() },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Spacer(Modifier.height(12.dp))
                    ProfileEditCard(
                        fullName = fullName,
                        onFullName = { fullName = it },
                        email = email,
                        onEmail = { email = it },
                        phone = phone,
                        onPhone = { phone = it },
                        preferredLocale = preferredLocale,
                        onPreferredLocale = { preferredLocale = it },
                        currentPassword = currentPassword,
                        onCurrentPassword = { currentPassword = it },
                        newPassword = newPassword,
                        onNewPassword = { newPassword = it },
                        saving = saving,
                        savingPassword = savingPassword,
                        onSave = { saveProfile() },
                        onSavePassword = { savePassword() },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Spacer(Modifier.height(16.dp))
                    NotificationPreferencesCard(
                        prefs = prefs,
                        saving = savingPrefs,
                        onChange = { updatePrefs(it) },
                    )
                    Spacer(Modifier.height(24.dp))
                    Text(
                        "Cerrar sesión",
                        color = GymlyColors.Error,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 15.sp,
                        modifier = Modifier.clickable(onClick = { doLogout() }),
                    )
                } else {
                    if (stacked) {
                        ProfileSummaryCard(
                            me = me,
                            photoRevision = photoRevision,
                            uploadingPhoto = uploadingPhoto,
                            onPickPhoto = { uploadPhoto() },
                            onClearPhoto = { clearPhoto() },
                            modifier = summaryWidth,
                        )
                        Spacer(Modifier.height(12.dp))
                        ProfileEditCard(
                            fullName = fullName,
                            onFullName = { fullName = it },
                            email = email,
                            onEmail = { email = it },
                            phone = phone,
                            onPhone = { phone = it },
                            preferredLocale = preferredLocale,
                            onPreferredLocale = { preferredLocale = it },
                            currentPassword = currentPassword,
                            onCurrentPassword = { currentPassword = it },
                            newPassword = newPassword,
                            onNewPassword = { newPassword = it },
                            saving = saving,
                            savingPassword = savingPassword,
                            onSave = { saveProfile() },
                            onSavePassword = { savePassword() },
                            modifier = Modifier.fillMaxWidth(),
                        )
                    } else {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top,
                        ) {
                            ProfileSummaryCard(
                                me = me,
                                photoRevision = photoRevision,
                                uploadingPhoto = uploadingPhoto,
                                onPickPhoto = { uploadPhoto() },
                                onClearPhoto = { clearPhoto() },
                                modifier = summaryWidth,
                            )
                            ProfileEditCard(
                                fullName = fullName,
                                onFullName = { fullName = it },
                                email = email,
                                onEmail = { email = it },
                                phone = phone,
                                onPhone = { phone = it },
                                preferredLocale = preferredLocale,
                                onPreferredLocale = { preferredLocale = it },
                                currentPassword = currentPassword,
                                onCurrentPassword = { currentPassword = it },
                                newPassword = newPassword,
                                onNewPassword = { newPassword = it },
                                saving = saving,
                                savingPassword = savingPassword,
                                onSave = { saveProfile() },
                                onSavePassword = { savePassword() },
                                modifier = Modifier.weight(1f),
                            )
                        }
                    }

                    Spacer(Modifier.height(16.dp))
                    RecentNotificationsCard(
                        items = notifications,
                        onMarkRead = { markRead(it) },
                    )
                    Spacer(Modifier.height(12.dp))
                    NotificationPreferencesCard(
                        prefs = prefs,
                        saving = savingPrefs,
                        onChange = { updatePrefs(it) },
                    )
                }
                // Sistema: términos y compromisos del socio (web y app).
                if (termsRequired) {
                Spacer(Modifier.height(16.dp))
                androidx.compose.material3.Card(
                    modifier = Modifier.fillMaxWidth().clickable(onClick = { showTerms = true }),
                ) {
                    Column(Modifier.padding(16.dp)) {
                        Text("Sistema", color = GymlyColors.TextMuted, fontSize = 12.sp)
                        Spacer(Modifier.height(4.dp))
                        Text(
                            "Términos y compromisos",
                            color = GymlyColors.Text,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 15.sp,
                        )
                        Text(
                            "Lee los términos, revisa tu estado y tu permiso de WhatsApp.",
                            color = GymlyColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                }
                }
                Spacer(Modifier.height(GymlySpace.xl))
            }
        }
    }
}

@Composable
private fun ProfileSummaryCard(
    me: PortalMe?,
    photoRevision: Int,
    uploadingPhoto: Boolean,
    onPickPhoto: () -> Unit,
    onClearPhoto: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val hasPhoto = !me?.photoUrl.isNullOrBlank()
    val displayUrl = me?.photoUrl?.takeIf { it.isNotBlank() }?.let { base ->
        val sep = if (base.contains('?')) '&' else '?'
        "$base${sep}v=$photoRevision"
    }

    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(horizontal = 22.dp, vertical = 28.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier
                .size(120.dp)
                .clip(CircleShape)
                .background(GymlyColors.Outline.copy(alpha = 0.25f))
                .border(2.dp, GymlyColors.Outline.copy(alpha = 0.4f), CircleShape)
                .clickable(enabled = !uploadingPhoto, onClick = onPickPhoto),
            contentAlignment = Alignment.Center,
        ) {
            if (!displayUrl.isNullOrBlank()) {
                AsyncImage(
                    model = displayUrl,
                    contentDescription = "Foto de perfil",
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop,
                )
            } else {
                Text(
                    initials(me?.displayName ?: me?.firstName),
                    fontWeight = FontWeight.Bold,
                    fontSize = 36.sp,
                    color = GymlyColors.Primary,
                )
            }
            if (uploadingPhoto) {
                Box(
                    Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.35f)),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator(
                        Modifier.size(28.dp),
                        color = Color.White,
                        strokeWidth = 2.dp,
                    )
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        PhotoActionBar(
            hasPhoto = hasPhoto,
            enabled = !uploadingPhoto,
            onUpload = onPickPhoto,
            onRemove = if (hasPhoto) onClearPhoto else null,
            accent = GymlyColors.Primary,
            error = GymlyColors.Error,
            uploadTooltip = if (hasPhoto) "Cambiar foto" else "Subir foto",
            removeTooltip = "Eliminar foto",
        )
        Spacer(Modifier.height(16.dp))
        Text(
            me?.displayName?.ifBlank { "—" } ?: "—",
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
            color = GymlyColors.Text,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(4.dp))
        Text(
            me?.branchName?.takeIf { it.isNotBlank() } ?: "Sin sede asignada",
            color = GymlyColors.TextMuted,
            fontSize = 13.sp,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun ProfileEditCard(
    fullName: String,
    onFullName: (String) -> Unit,
    email: String,
    onEmail: (String) -> Unit,
    phone: String,
    onPhone: (String) -> Unit,
    preferredLocale: String,
    onPreferredLocale: (String) -> Unit,
    currentPassword: String,
    onCurrentPassword: (String) -> Unit,
    newPassword: String,
    onNewPassword: (String) -> Unit,
    saving: Boolean,
    savingPassword: Boolean,
    onSave: () -> Unit,
    onSavePassword: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(22.dp)
            .widthIn(max = 720.dp),
    ) {
        Text("Editar perfil", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(16.dp))

        ProfileField("Nombre", fullName, onFullName)
        Spacer(Modifier.height(12.dp))
        ProfileField(
            label = "Correo",
            value = email,
            onValue = onEmail,
            keyboardType = KeyboardType.Email,
        )
        Spacer(Modifier.height(12.dp))
        ProfileField(
            label = "Teléfono",
            value = phone,
            onValue = onPhone,
            keyboardType = KeyboardType.Phone,
        )
        Spacer(Modifier.height(12.dp))
        Text("Idioma", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(6.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            LocaleChip("Español", preferredLocale.lowercase() != "en") { onPreferredLocale("es") }
            LocaleChip("English", preferredLocale.lowercase() == "en") { onPreferredLocale("en") }
        }

        Spacer(Modifier.height(18.dp))
        Button(
            onClick = onSave,
            enabled = !saving,
            colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
            shape = RoundedCornerShape(10.dp),
        ) {
            if (saving) {
                CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
            } else {
                Text("Guardar", fontWeight = FontWeight.SemiBold)
            }
        }

        Spacer(Modifier.height(28.dp))
        Text("Cambiar contraseña", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(12.dp))
        ProfileField(
            label = "Contraseña actual",
            value = currentPassword,
            onValue = onCurrentPassword,
            isPassword = true,
        )
        Spacer(Modifier.height(12.dp))
        ProfileField(
            label = "Nueva contraseña (mín. 8)",
            value = newPassword,
            onValue = onNewPassword,
            isPassword = true,
        )
        Spacer(Modifier.height(14.dp))
        Button(
            onClick = onSavePassword,
            enabled = !savingPassword,
            colors = ButtonDefaults.buttonColors(
                containerColor = GymlyColors.Outline.copy(alpha = 0.35f),
                contentColor = GymlyColors.Text,
            ),
            shape = RoundedCornerShape(10.dp),
        ) {
            if (savingPassword) {
                CircularProgressIndicator(Modifier.size(18.dp), color = GymlyColors.Primary, strokeWidth = 2.dp)
            } else {
                Text("Actualizar contraseña", fontWeight = FontWeight.SemiBold)
            }
        }
    }
}

@Composable
private fun LocaleChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Text(
        label,
        color = if (selected) GymlyColors.OnPrimary else GymlyColors.Text,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(if (selected) GymlyColors.Primary else GymlyColors.Surface)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(16.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
    )
}

@Composable
private fun ProfileField(
    label: String,
    value: String,
    onValue: (String) -> Unit,
    placeholder: String = "",
    keyboardType: KeyboardType = KeyboardType.Text,
    isPassword: Boolean = false,
) {
    Text(label, fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
    Spacer(Modifier.height(6.dp))
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onValue,
        singleLine = true,
        placeholder = if (placeholder.isNotEmpty()) ({ Text(placeholder) }) else null,
        visualTransformation = if (isPassword && !visible) {
            androidx.compose.ui.text.input.PasswordVisualTransformation()
        } else {
            androidx.compose.ui.text.input.VisualTransformation.None
        },
        trailingIcon = if (isPassword) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = keyboardType),
        modifier = Modifier.fillMaxWidth(),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = GymlyColors.Primary,
            unfocusedBorderColor = GymlyColors.Outline,
        ),
        shape = RoundedCornerShape(10.dp),
    )
}

private fun splitFullName(raw: String): Pair<String, String> {
    val parts = raw.trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
    if (parts.isEmpty()) return "" to ""
    if (parts.size == 1) return parts[0] to parts[0]
    return parts.first() to parts.drop(1).joinToString(" ")
}

private fun initials(name: String?): String {
    val parts = name.orEmpty().trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
    if (parts.isEmpty()) return "?"
    return parts.take(2).joinToString("") { it.first().uppercaseChar().toString() }
}

@Composable
private fun RecentNotificationsCard(
    items: List<PortalClientNotification>,
    onMarkRead: (PortalClientNotification) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(20.dp),
    ) {
        Text("Notificaciones recientes", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(12.dp))
        if (items.isEmpty()) {
            Text("No hay notificaciones recientes.", color = GymlyColors.TextMuted, fontSize = 13.sp)
        } else {
            items.forEachIndexed { index, item ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .clickable { onMarkRead(item) }
                        .padding(vertical = 10.dp),
                    verticalAlignment = Alignment.Top,
                ) {
                    Box(
                        Modifier
                            .padding(top = 4.dp)
                            .size(18.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .border(
                                1.5.dp,
                                if (item.read) GymlyColors.Outline else GymlyColors.Primary,
                                RoundedCornerShape(4.dp),
                            )
                            .background(
                                if (item.read) GymlyColors.Primary.copy(alpha = 0.15f)
                                else Color.Transparent,
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (item.read) {
                            Icon(
                                Icons.Outlined.Check,
                                contentDescription = null,
                                tint = GymlyColors.Primary,
                                modifier = Modifier.size(14.dp),
                            )
                        }
                    }
                    Spacer(Modifier.width(12.dp))
                    Column(Modifier.weight(1f)) {
                        Text(
                            item.title,
                            color = GymlyColors.Text,
                            fontSize = 14.sp,
                            fontWeight = if (item.read) FontWeight.Normal else FontWeight.SemiBold,
                        )
                        Spacer(Modifier.height(4.dp))
                        Text(item.relativeLabel, color = GymlyColors.TextMuted, fontSize = 12.sp)
                    }
                }
                if (index < items.lastIndex) {
                    Box(
                        Modifier
                            .fillMaxWidth()
                            .height(1.dp)
                            .background(GymlyColors.Outline.copy(alpha = 0.35f)),
                    )
                }
            }
        }
    }
}

@Composable
private fun NotificationPreferencesCard(
    prefs: PortalNotificationPreferences,
    saving: Boolean,
    onChange: (PortalNotificationPreferences) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(20.dp),
    ) {
        Text("Notificaciones", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(6.dp))
        Text(
            "Elige de qué quieres que te avisemos a ti en particular.",
            color = GymlyColors.TextMuted,
            fontSize = 13.sp,
        )
        Spacer(Modifier.height(14.dp))
        PreferenceToggleRow(
            label = "Recordatorios de pago",
            checked = prefs.paymentReminders,
            enabled = !saving,
            onChecked = { onChange(prefs.copy(paymentReminders = it)) },
        )
        PreferenceToggleRow(
            label = "Recordatorios de clase",
            checked = prefs.classReminders,
            enabled = !saving,
            onChecked = { onChange(prefs.copy(classReminders = it)) },
        )
        PreferenceToggleRow(
            label = "Promociones",
            checked = prefs.promotions,
            enabled = !saving,
            onChecked = { onChange(prefs.copy(promotions = it)) },
        )
        PreferenceToggleRow(
            label = "Mensajes del personal",
            checked = prefs.staffMessages,
            enabled = !saving,
            onChecked = { onChange(prefs.copy(staffMessages = it)) },
        )
    }
}

@Composable
private fun PreferenceToggleRow(
    label: String,
    checked: Boolean,
    enabled: Boolean,
    onChecked: (Boolean) -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(vertical = 10.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(label, color = GymlyColors.Text, fontSize = 14.sp, modifier = Modifier.weight(1f).padding(end = 12.dp))
        GymlySwitch(checked = checked, enabled = enabled, onCheckedChange = onChecked)
    }
}

@Composable
private fun GymlySwitch(
    checked: Boolean,
    enabled: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    val trackColor = when {
        !enabled -> GymlyColors.Outline.copy(alpha = 0.35f)
        checked -> GymlyColors.Primary
        else -> GymlyColors.Outline.copy(alpha = 0.7f)
    }
    Box(
        Modifier
            .size(width = 48.dp, height = 28.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(trackColor)
            .clickable(enabled = enabled) { onCheckedChange(!checked) }
            .padding(3.dp),
        contentAlignment = if (checked) Alignment.CenterEnd else Alignment.CenterStart,
    ) {
        Box(
            Modifier
                .size(22.dp)
                .clip(CircleShape)
                .background(Color.White),
        )
    }
}
